import { css, Styles, withStyles } from '@recruit-robin/robin-theme/web-styles';

import { af, compactQuery, expandedQuery, floatingSurfaceCss } from '../theme';

const styles = {
  /* ---------- step head ---------- */
  stepHead: css`
    margin-bottom: ${af.sp8};

    ${compactQuery} {
      margin-bottom: ${af.sp6};
    }

    ${expandedQuery} {
      /* One title-to-content value on every screen, the question's included. */
      margin-bottom: ${af.sp10};
    }
  `,
  titleRow: css`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${af.sp3};
  `,
  stepTitle: css`
    margin-bottom: ${af.sp3};
  `,
  /** Roughly 46ch keeps supporting copy off the pane's full measure. */
  stepBody: css`
    max-width: 46ch;
  `,
  /**
   * The centred variant, for a screen that asks something rather than continuing a flow — the
   * switch confirmation, and the past-the-cut-off card.
   */
  stepHeadCentered: css`
    text-align: center;
  `,
  /** Keeps the body's 46ch measure while centring the block it sits in. */
  stepBodyCentered: css`
    margin-left: auto;
    margin-right: auto;
  `,
  durationChip: css`
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 5px;
    box-shadow: inset 0 0 0 1px ${af.line};
    color: ${af.muted};
    padding: 6px 12px;
    border-radius: ${af.rPill};
    white-space: nowrap;

    svg {
      width: 13px;
      height: 13px;
    }
  `,

  /* ---------- role box ---------- */
  /**
   * The one flat surface: a panel on the stage rather than a control lifting off it, so
   * `--af-fill` ground and no shadow. It clips the scooter as it rides in and out.
   */
  roleBox: css`
    display: flex;
    align-items: center;
    gap: ${af.sp4};
    border-radius: ${af.rCard};
    padding: 18px ${af.sp5};
    background: ${af.fill};
    margin-bottom: 22px;
    overflow: hidden;
  `,
  roleText: css`
    flex: 1;
    min-width: 0;
  `,
  roleEyebrow: css`
    margin-bottom: 7px;
  `,
  roleTitle: css`
    margin-bottom: 5px;
  `,

  /* ---------- details panel ---------- */
  /** A `dl`, so the browser's own list margins have to come off. */
  details: css`
    ${floatingSurfaceCss};
    padding: 6px ${af.sp5};
    margin: ${af.sp4} 0 0;
  `,
  detailRow: css`
    display: flex;
    justify-content: space-between;
    gap: ${af.sp4};
    padding: ${af.sp2} 0;

    & + & {
      border-top: 1px solid ${af.lineSoft};
    }
  `,
  detailKey: css`
    flex: 0 0 auto;
  `,
  /**
   * Wraps rather than overflows, however long the value is.
   *
   * A flex item refuses to shrink past its longest unbreakable word, so an e-mail address — one
   * word with no spaces in it — pushed the row wider than the card and ran off the right edge on
   * a phone. `min-width: 0` lets the item shrink; `anywhere` lets the word itself break, and is
   * the value that also counts toward the intrinsic minimum, which `break-word` does not. That
   * one is kept before it as the fallback for engines without `anywhere`.
   */
  detailValue: css`
    margin: 0;
    text-align: right;
    min-width: 0;
    overflow-wrap: break-word;
    overflow-wrap: anywhere;
  `,

  /* ---------- reasons ---------- */
  reasons: css`
    ${floatingSurfaceCss};
    padding: 18px ${af.sp5};
    margin-top: ${af.sp5};
  `,
  reasonsLabel: css`
    margin-bottom: 11px;
  `,
  reasonsList: css`
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 10px;
  `,
  reasonItem: css`
    padding-left: 18px;
    position: relative;
    /*
     * The row's own line box, matching the handover's list. Without it the item inherits the
     * app's default leading, whose taller strut pushes the first line down while the dot below
     * stays put — the dot then reads as floating above the text it belongs to.
     */
    font-size: 14px;
    line-height: 1.55;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 8px;
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: ${af.muted};
    }
  `,

  /* ---------- footer ---------- */
  footer: css`
    max-width: 560px;
    /* An auto top margin pushes the footer down; on expanded it also splits the free
       space with the wrap's leading element, which is what centres the content group. */
    margin: auto auto 0;
    padding: ${af.sp4} ${af.sp6} 0;
    border-top: 1px solid ${af.lineSoft};
    text-align: center;
    line-height: 1.8;

    ${compactQuery} {
      padding: ${af.sp4} 14px 0;
    }
  `,
  footerLink: css`
    color: ${af.muted};
  `,
  /**
   * The mark sits on the text's baseline row rather than after it: the flow sets every svg
   * to `display: block`, which would otherwise drop it onto a line of its own inside this
   * centred, 1.8-line-height footer.
   */
  footerPoweredBy: css`
    display: inline-flex;
    align-items: center;
    gap: 5px;
  `,
} satisfies Styles<void>;

export const useStyles = withStyles(styles);
